'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { X, Scissors, User, Phone, Mail, Info } from 'lucide-react';

import {
  useServiciosPorEmpresa,
  useServiciosPorUsuario,
} from '@/components/hooks/useServicios';
import { useHorariosPorUsuario } from '@/components/hooks/useHorarios';
import { usePlazasDisponibles } from '@/components/hooks/usePlazasDisponibles';
import { useTrabajadoresPorServicio } from '@/components/hooks/useTrabajadorDetalles';
import { useClientes, Cliente } from '@/components/hooks/useClientes';
import { crearCita } from '@/components/hooks/useCitas';
import { ClientSearchBox } from '@/components/pages/shared/ClientSearchBox';
import type { Horario } from '@/components/types/typeCalendar';

export type NewCita = {
  trabajador: string;
  servicio: string;
  telefono: string;
  nombre?: string;
  email: string;
  observaciones: string;
  fecha: string;
  fechaFin?: string;
  hora: string;
  horaFin: string;
  num_clientes: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  initialData: NewCita;
  onChangeCita?: (cita: NewCita) => void;
  idEmpresa: string | null;
  idUsuario: string;
  onCitaCreada?: () => void;
  incluirTrabajador?: boolean;
  trabajadores?: { id_usuario: string; nombre: string }[];
};

const diasSemanaMap: Record<number, string> = {
  0: 'domingo',
  1: 'lunes',
  2: 'martes',
  3: 'miercoles',
  4: 'jueves',
  5: 'viernes',
  6: 'sabado',
};

const stripAccents = (s: string) =>
  s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const formatTime = (h: string, m: string) =>
  `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;

const diaSiguiente = (fecha: string): string => {
  const d = new Date(`${fecha}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

const calcularHoraFin = (hora: string, duracion: number): string => {
  if (!hora || !duracion) return '';
  const [h, m] = hora.split(':').map(Number);
  const inicio = new Date();
  inicio.setHours(h, m, 0, 0);
  const fin = new Date(inicio.getTime() + duracion * 60000);
  return formatTime(String(fin.getHours()), String(fin.getMinutes()));
};

const emptyCita: NewCita = {
  trabajador: '',
  servicio: '',
  telefono: '',
  email: '',
  observaciones: '',
  fecha: '',
  fechaFin: '',
  hora: '',
  horaFin: '',
  num_clientes: 1,
};

// ─── Fuera del componente principal ──────────────────────────────────────────

const Field = ({
  label,
  optional,
  required,
  children,
}: {
  label: string;
  optional?: boolean;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <div>
    <label className="block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
      {optional && (
        <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">
          (opcional)
        </span>
      )}
    </label>
    {children}
  </div>
);

const DialogTopBar = ({
  step,
  onClose,
}: {
  step: 1 | 2;
  onClose: () => void;
}) => (
  <div className="flex items-center gap-3 px-4 pt-3 pb-2">
    <div className="flex flex-1 gap-1.5">
      <div className="h-1 flex-1 rounded-full bg-[#103254] dark:bg-blue-400" />
      <div
        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
          step === 2
            ? 'bg-[#103254] dark:bg-blue-400'
            : 'bg-gray-200 dark:bg-white/10'
        }`}
      />
    </div>
    <button
      onClick={onClose}
      className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
      aria-label="Cerrar"
    >
      <X className="w-5 h-5" />
    </button>
  </div>
);

const ResumenCard = ({
  servicio,
  trabajadorNombre,
  horas,
  minutos,
  precio,
  precioLabel,
  nombre,
  telefono,
  email,
}: {
  servicio: string;
  trabajadorNombre?: string;
  horas: number;
  minutos: number;
  precio: string;
  precioLabel?: string;
  nombre?: string;
  telefono?: string;
  email?: string;
}) => (
  <div className="rounded-xl border border-[#103254]/20 dark:border-blue-400/20 bg-[#103254]/5 dark:bg-blue-400/5 p-3 space-y-2.5">
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-[#103254] dark:bg-blue-500 flex items-center justify-center shrink-0">
        <Scissors className="w-3.5 h-3.5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#103254] dark:text-blue-300 truncate">
          {servicio}
        </p>
        {trabajadorNombre && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
            <User className="w-3 h-3 shrink-0" />
            {trabajadorNombre}
          </p>
        )}
      </div>
      <div className="text-right shrink-0">
        <p className="text-xs font-medium text-[#103254] dark:text-blue-300">
          {horas > 0 && `${horas}h `}
          {minutos}min
        </p>
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {precio} €{precioLabel ? ` ${precioLabel}` : ''}
        </p>
      </div>
    </div>

    {(nombre || telefono || email) && (
      <div className="pt-2 border-t border-[#103254]/10 dark:border-blue-400/10 flex flex-wrap gap-x-4 gap-y-1">
        {nombre && (
          <span className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400">
            <User className="w-3 h-3 shrink-0 text-[#103254] dark:text-blue-400" />
            {nombre}
          </span>
        )}
        {telefono && (
          <span className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400">
            <Phone className="w-3 h-3 shrink-0 text-[#103254] dark:text-blue-400" />
            {telefono}
          </span>
        )}
        {email && (
          <span className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-400 truncate">
            <Mail className="w-3 h-3 shrink-0 text-[#103254] dark:text-blue-400" />
            {email}
          </span>
        )}
      </div>
    )}
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
export default function NewAppointmentDialog({
  open,
  onClose,
  initialData,
  onChangeCita,
  idEmpresa,
  idUsuario,
  onCitaCreada,
  incluirTrabajador = false,
}: Props) {
  const { t } = useTranslation();

  const [step, setStep] = useState<1 | 2>(1);
  const [expandObservaciones, setExpandObservaciones] = useState(false);
  const [overflowDialog, setOverflowDialog] = useState(false);
  const [overflowInfo, setOverflowInfo] = useState<{
    allowedEnd: string;
  } | null>(null);
  const [seguirNormal, setSeguirNormal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cita, setCita] = useState<NewCita>(initialData);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { servicios: serviciosEmpresa = [] } = useServiciosPorEmpresa(
    incluirTrabajador ? idEmpresa! : ''
  );
  const { servicios: serviciosTrabajador = [] } = useServiciosPorUsuario(
    incluirTrabajador ? '' : idUsuario,
    idEmpresa!
  );
  const serviciosDisponibles = incluirTrabajador
    ? serviciosEmpresa
    : serviciosTrabajador;
  const { trabajadores: trabajadoresServicio = [] } =
    useTrabajadoresPorServicio(
      incluirTrabajador ? cita.servicio : '',
      idEmpresa || undefined
    );
  const { horarios } = useHorariosPorUsuario(
    cita.trabajador || idUsuario,
    idEmpresa!
  );
  const { getClientesEmpresa } = useClientes();
  const [clientesEmpresa, setClientesEmpresa] = useState<Cliente[]>([]);

  useEffect(() => {
    if (!idEmpresa) return;
    getClientesEmpresa(idEmpresa).then(setClientesEmpresa);
  }, [idEmpresa, getClientesEmpresa]);

  useEffect(() => {
    if (open) {
      setCita({
        ...initialData,
        trabajador: incluirTrabajador ? initialData.trabajador : idUsuario,
      });
      setStep(1);
      setOverflowDialog(false);
      setSeguirNormal(false);
      setExpandObservaciones(false);
    } else {
      setCita(emptyCita);
      onChangeCita?.(emptyCita);
      setStep(1);
      setOverflowDialog(false);
      setSeguirNormal(false);
      setExpandObservaciones(false);
    }
  }, [open]);

  const citaRef = useRef(cita);
  useEffect(() => {
    citaRef.current = cita;
  }, [cita]);

  const updateCita = useCallback(
    (updated: Partial<NewCita>) => {
      if (updated.servicio !== undefined) setSeguirNormal(false);
      const nueva = { ...citaRef.current, ...updated };
      setCita(nueva);
      onChangeCita?.(nueva);
    },
    [onChangeCita]
  );

  const servicioSeleccionado = serviciosDisponibles.find(
    (s) => s.id_servicio === cita.servicio
  );

  const plazasDisponibles =
    servicioSeleccionado && servicioSeleccionado.cliente_max > 1
      ? usePlazasDisponibles(
          cita.servicio,
          cita.fecha,
          cita.hora,
          servicioSeleccionado.cliente_max
        )
      : null;

  // Servicios por horas: las horas salen de hora/horaFin de la cita.
  const porHoras = !!servicioSeleccionado?.reserva_por_horas;
  const horasMin = servicioSeleccionado?.horas_min ?? 1;
  const horasMax = servicioSeleccionado?.horas_max ?? horasMin;
  const horasCita = (() => {
    if (!porHoras) return 1;
    const [h1, m1] = (cita.hora || '0:0').split(':').map(Number);
    const [h2, m2] = (cita.horaFin || '0:0').split(':').map(Number);
    const diff = h2 * 60 + m2 - (h1 * 60 + m1);
    const n = Math.round((diff > 0 ? diff : diff + 1440) / 60);
    return Math.min(horasMax, Math.max(horasMin, n || horasMin));
  })();
  const duracionCita = servicioSeleccionado
    ? porHoras
      ? horasCita * 60
      : servicioSeleccionado.duracion_minutos
    : 0;

  // Servicio por horas: hora fin y, si pasa de medianoche, fecha fin.
  const finPorHoras = (fecha: string, hora: string, minutos: number) => {
    const horaFin = calcularHoraFin(hora, minutos);
    const fechaFin =
      fecha && horaFin && horaFin <= hora.slice(0, 5) ? diaSiguiente(fecha) : '';
    return { horaFin, fechaFin };
  };

  const horas = Math.floor(duracionCita / 60);
  const minutosRestantes = duracionCita % 60;
  const precioTexto = servicioSeleccionado
    ? (
        (servicioSeleccionado.precio ?? 0) *
        (servicioSeleccionado.precio_aplica === 'Por persona'
          ? cita.num_clientes || 1
          : 1) *
        (porHoras ? horasCita : 1)
      ).toFixed(2)
    : '0.00';
  const trabajadorNombre = trabajadoresServicio.find(
    (tw) => tw.id_usuario === cita.trabajador
  )?.nombre;

  // ── Overflow ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (porHoras) {
      setOverflowDialog(false);
      return;
    }
    if (seguirNormal || !cita.fecha || !cita.hora || !cita.horaFin) return;
    const start = new Date(`${cita.fecha}T${cita.hora}:00`);
    const end = new Date(`${cita.fecha}T${cita.horaFin}:00`);
    if (end.getTime() <= start.getTime()) {
      setOverflowInfo({ allowedEnd: '' });
      setOverflowDialog(true);
      return;
    }
    const [y, M, d] = cita.fecha.split('-').map(Number);
    const key = diasSemanaMap[new Date(y, M - 1, d).getDay()];
    const franjas = horarios.filter(
      (h: Horario) => stripAccents(h.dia_semana.toLowerCase()) === key
    );
    if (!franjas.length) return;
    const maxEnd = franjas.reduce(
      (max: string, h: Horario) => (h.hora_fin > max ? h.hora_fin : max),
      franjas[0].hora_fin
    );
    if (cita.horaFin > maxEnd) {
      setOverflowInfo({ allowedEnd: maxEnd });
      setOverflowDialog(true);
    } else setOverflowDialog(false);
  }, [cita.fecha, cita.hora, cita.horaFin, horarios, seguirNormal, porHoras]);

  // Justo después del useEffect de sincronización de `open`
  useEffect(() => {
    if (
      trabajadoresServicio.length === 1 &&
      cita.servicio &&
      cita.trabajador !== trabajadoresServicio[0].id_usuario
    ) {
      updateCita({ trabajador: trabajadoresServicio[0].id_usuario });
    }
  }, [trabajadoresServicio]);

  const handleMantener = () => {
    setOverflowDialog(false);
    setSeguirNormal(true);
  };

  const handleEditar = () => {
    setSeguirNormal(true);
    const servicioSel = serviciosDisponibles.find(
      (s) => s.id_servicio === cita.servicio
    );
    if (!servicioSel) {
      setOverflowDialog(false);
      return;
    }
    const totalMinutos = porHoras ? duracionCita : servicioSel.duracion_minutos;
    const franjasHoy = horarios
      .filter(
        (h: Horario) =>
          stripAccents(h.dia_semana.toLowerCase()) ===
          diasSemanaMap[new Date(cita.fecha).getDay()]
      )
      .sort((a: Horario, b: Horario) =>
        a.hora_inicio.localeCompare(b.hora_inicio)
      );
    const [h0, m0] = cita.hora.split(':').map(Number);
    let usadosHoy = 0,
      startMinOriginal = h0 * 60 + m0;
    for (const fr of franjasHoy) {
      const [hi, mi] = fr.hora_inicio.split(':').map(Number);
      const [hf, mf] = fr.hora_fin.split(':').map(Number);
      const startMin = Math.max(startMinOriginal, hi * 60 + mi);
      if (hf * 60 + mf > startMin) {
        usadosHoy += hf * 60 + mf - startMin;
        if (usadosHoy >= totalMinutos) {
          usadosHoy = totalMinutos;
          break;
        }
        startMinOriginal = hf * 60 + mf;
      }
    }
    let restante = totalMinutos - Math.min(totalMinutos, usadosHoy);
    let cursorDate = new Date(cita.fecha);
    let nuevaFechaFin = cita.fecha,
      nuevaHoraFin = cita.horaFin;
    while (restante > 0) {
      cursorDate.setDate(cursorDate.getDate() + 1);
      const franjas = horarios
        .filter(
          (h: Horario) =>
            stripAccents(h.dia_semana.toLowerCase()) ===
            diasSemanaMap[cursorDate.getDay()]
        )
        .sort((a: Horario, b: Horario) =>
          a.hora_inicio.localeCompare(b.hora_inicio)
        );
      for (const fr of franjas) {
        const [hi, mi] = fr.hora_inicio.split(':').map(Number);
        const [hf, mf] = fr.hora_fin.split(':').map(Number);
        const dur = hf * 60 + mf - (hi * 60 + mi);
        if (Math.min(restante, dur) >= restante) {
          const finMin = hi * 60 + mi + restante;
          nuevaFechaFin = cursorDate.toISOString().slice(0, 10);
          nuevaHoraFin = `${String(Math.floor(finMin / 60)).padStart(
            2,
            '0'
          )}:${String(finMin % 60).padStart(2, '0')}`;
          restante = 0;
          break;
        } else {
          restante -= dur;
        }
      }
    }
    if (!nuevaFechaFin?.trim()) nuevaFechaFin = cita.fecha;
    updateCita({ fechaFin: nuevaFechaFin, horaFin: nuevaHoraFin });
    setOverflowDialog(false);
  };

  const handleAddNote = () => {
    setExpandObservaciones(true);
    if (!cita.observaciones) updateCita({ observaciones: '' });
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const guardarCita = async () => {
    if (overflowDialog) return;
    setLoading(true);
    const resp = await crearCita(
      porHoras ? { ...cita, desbordamiento: false } : cita
    );
    if (!resp.ok) {
      toast.error(t('newAppointmentDialog.errorSave'));
      setLoading(false);
      return;
    }
    toast.success(t('newAppointmentDialog.successSave'));
    setCita(emptyCita);
    onChangeCita?.(emptyCita);
    setLoading(false);
    onCitaCreada?.();
  };

  const handleClose = () => {
    setCita(emptyCita);
    onChangeCita?.(emptyCita);
    setOverflowDialog(false);
    setSeguirNormal(false);
    setExpandObservaciones(false);
    setStep(1);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none h-[75vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          data-[state=open]:ease-out data-[state=closed]:ease-in
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-xl sm:mx-auto sm:h-auto sm:max-h-[94vh] sm:rounded-xl sm:shadow-xl
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
        "
      >
        <DialogTopBar step={step} onClose={handleClose} />

        <DialogHeader className="px-5 pb-1 shrink-0">
          <DialogTitle className="text-sm font-semibold text-[#103254] dark:text-blue-400">
            {t('newAppointmentDialog.title')}
          </DialogTitle>
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            {step === 1
              ? t(
                  'newAppointmentDialog.step1Label',
                  'Paso 1 de 2 — Datos del cliente'
                )
              : t(
                  'newAppointmentDialog.step2Label',
                  'Paso 2 de 2 — Fecha y hora'
                )}
          </p>
        </DialogHeader>

        <div className="px-5 py-3 space-y-3 text-sm text-gray-700 dark:text-gray-300 flex-1 overflow-y-auto">
          {/* ══ PASO 1 ══════════════════════════════════════════════════════ */}
          {step === 1 && (
            <>
              <Field label={t('newAppointmentDialog.labelService')} required>
                <Select
                  value={cita.servicio}
                  onValueChange={(id) => {
                    const s = serviciosDisponibles.find(
                      (x) => x.id_servicio === id
                    );
                    updateCita(
                      s?.reserva_por_horas
                        ? {
                            servicio: id,
                            ...finPorHoras(
                              cita.fecha,
                              cita.hora,
                              (s.horas_min ?? 1) * 60
                            ),
                          }
                        : {
                            servicio: id,
                            horaFin: calcularHoraFin(
                              cita.hora,
                              s?.duracion_minutos || 0
                            ),
                            fechaFin: '',
                          }
                    );
                  }}
                >
                  <SelectTrigger
                    className={`h-12 text-sm dark:bg-white/5 dark:text-white ${
                      cita.servicio
                        ? 'dark:border-white/10'
                        : 'border-red-300 dark:border-red-500/50 focus:ring-red-400'
                    }`}
                  >
                    <SelectValue
                      placeholder={t('newAppointmentDialog.selectPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent className="z-[70]">
                    {serviciosDisponibles.map((s) => (
                      <SelectItem
                        key={s.id_servicio}
                        value={s.id_servicio}
                        className="text-sm"
                      >
                        {s.servicio}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {servicioSeleccionado ? (
                  <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                    {horas > 0 && `${horas}h `}
                    {minutosRestantes}m — {precioTexto} €
                    {servicioSeleccionado.precio_aplica === 'Por persona' &&
                      ` ${t('newAppointmentDialog.perPerson')}`}
                  </p>
                ) : (
                  <p className="mt-1 text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
                    <Info className="w-3 h-3 shrink-0" />
                    {t(
                      'newAppointmentDialog.serviceRequired',
                      'Selecciona un servicio para continuar'
                    )}
                  </p>
                )}
              </Field>

              {servicioSeleccionado?.requiere_trabajador &&
                incluirTrabajador && (
                  <Field label={t('newAppointmentDialog.labelWorker')} required>
                    <Select
                      value={cita.trabajador}
                      onValueChange={(v) => updateCita({ trabajador: v })}
                    >
                      <SelectTrigger className="h-12 text-sm dark:bg-white/5 dark:border-white/10 dark:text-white">
                        <SelectValue
                          placeholder={t(
                            'newAppointmentDialog.selectPlaceholder'
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {trabajadoresServicio.map((tw) => (
                          <SelectItem
                            key={tw.id_usuario}
                            value={tw.id_usuario}
                            className="text-sm"
                          >
                            {tw.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}

              <Field label={t('newAppointmentDialog.labelClientName')} optional>
                <ClientSearchBox
                  clientes={clientesEmpresa}
                  placeholder={t(
                    'newAppointmentDialog.searchClientPlaceholder'
                  )}
                  displayField="nombre"
                  value={cita.nombre}
                  onChange={(v) => updateCita({ nombre: v })}
                  onSelect={(cli) =>
                    updateCita({
                      nombre: cli.nombre || '',
                      telefono: cli.telefono || '',
                      email: cli.email || '',
                    })
                  }
                />
              </Field>

              <Field label={t('newAppointmentDialog.labelPhone')} optional>
                <ClientSearchBox
                  clientes={clientesEmpresa}
                  placeholder={t('newAppointmentDialog.searchPhonePlaceholder')}
                  displayField="telefono"
                  value={cita.telefono}
                  onChange={(v) => updateCita({ telefono: v })}
                  onSelect={(cli) =>
                    updateCita({
                      telefono: cli.telefono || '',
                      email: cli.email || cita.email,
                      nombre: cli.nombre || '',
                    })
                  }
                />
              </Field>

              <Field label={t('newAppointmentDialog.labelEmail')} optional>
                <ClientSearchBox
                  clientes={clientesEmpresa}
                  placeholder={t('newAppointmentDialog.searchEmailPlaceholder')}
                  displayField="email"
                  value={cita.email}
                  onChange={(v) => updateCita({ email: v })}
                  onSelect={(cli) =>
                    updateCita({
                      telefono: cita.telefono || cli.telefono || '',
                      email: cli.email || '',
                    })
                  }
                />
              </Field>

              {/* OBSERVACIONES */}
              <div>
                {!expandObservaciones ? (
                  <button
                    type="button"
                    onClick={handleAddNote}
                    className="text-xs text-[#103254] dark:text-blue-400 underline hover:opacity-80"
                  >
                    {t('newAppointmentDialog.addNote')}
                  </button>
                ) : (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium text-[#103254] dark:text-blue-400">
                        {t('newAppointmentDialog.labelNote')}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setExpandObservaciones(false);
                          if (!cita.observaciones?.trim())
                            updateCita({ observaciones: '' });
                        }}
                        className="text-[11px] text-red-500 dark:text-red-400 underline hover:opacity-80"
                      >
                        {t('newAppointmentDialog.hideNote')}
                      </button>
                    </div>
                    <textarea
                      ref={textareaRef}
                      rows={3}
                      value={cita.observaciones}
                      onChange={(e) =>
                        updateCita({ observaciones: e.target.value })
                      }
                      placeholder={t('newAppointmentDialog.notePlaceholder')}
                      className="w-full border border-gray-300 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg p-2.5 text-sm resize-none focus:ring-2 focus:ring-[#103254] dark:focus:ring-blue-400"
                    />
                  </div>
                )}
              </div>

              {servicioSeleccionado && servicioSeleccionado.cliente_max > 1 && (
                <Field label={t('newAppointmentDialog.labelParticipants')}>
                  <input
                    type="number"
                    min={servicioSeleccionado.cliente_min || 1}
                    max={servicioSeleccionado.cliente_max}
                    value={cita.num_clientes}
                    onChange={(e) =>
                      updateCita({ num_clientes: Number(e.target.value) })
                    }
                    className="w-full border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white rounded-lg px-3 py-1.5 text-sm"
                  />
                  {plazasDisponibles !== null && (
                    <p className="text-[11px] text-green-600 dark:text-green-400 mt-1">
                      {t('newAppointmentDialog.availableSlots', {
                        n: plazasDisponibles,
                      })}
                    </p>
                  )}
                </Field>
              )}
            </>
          )}

          {/* ══ PASO 2 ══════════════════════════════════════════════════════ */}
          {step === 2 && (
            <>
              {servicioSeleccionado && (
                <ResumenCard
                  servicio={servicioSeleccionado.servicio}
                  trabajadorNombre={trabajadorNombre}
                  horas={horas}
                  minutos={minutosRestantes}
                  precio={precioTexto}
                  precioLabel={
                    servicioSeleccionado.precio_aplica === 'Por persona'
                      ? t('newAppointmentDialog.perPerson')
                      : undefined
                  }
                  nombre={cita.nombre}
                  telefono={cita.telefono}
                  email={cita.email}
                />
              )}

              <Field label={t('newAppointmentDialog.labelDate')}>
                <Input
                  type="date"
                  value={cita.fecha}
                  onChange={(e) =>
                    updateCita(
                      porHoras && cita.hora
                        ? {
                            fecha: e.target.value,
                            ...finPorHoras(e.target.value, cita.hora, duracionCita),
                          }
                        : { fecha: e.target.value }
                    )
                  }
                  className="h-10 text-sm border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
                />
              </Field>

              <div className="flex flex-col gap-3">
                <Field label={t('newAppointmentDialog.labelTimeStart')}>
                  <Input
                    type="time"
                    value={cita.hora}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':');
                      const start = formatTime(h, m);
                      updateCita(
                        porHoras
                          ? {
                              hora: start,
                              ...finPorHoras(cita.fecha, start, duracionCita),
                            }
                          : {
                              hora: start,
                              horaFin: calcularHoraFin(start, duracionCita),
                            }
                      );
                    }}
                    className="h-10 text-sm border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
                  />
                </Field>
                {porHoras && (
                  <Field label={t('newAppointmentDialog.labelHours', 'Horas')}>
                    <select
                      value={horasCita}
                      onChange={(e) =>
                        updateCita(
                          finPorHoras(
                            cita.fecha,
                            cita.hora,
                            Number(e.target.value) * 60
                          )
                        )
                      }
                      className="w-full h-10 border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white rounded-lg px-3 text-sm"
                    >
                      {Array.from(
                        { length: horasMax - horasMin + 1 },
                        (_, i) => horasMin + i
                      ).map((n) => (
                        <option key={n} value={n}>
                          {n} h
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
                <Field label={t('newAppointmentDialog.labelTimeEnd')}>
                  <Input
                    type="time"
                    value={cita.horaFin}
                    disabled={porHoras}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':');
                      updateCita({ horaFin: formatTime(h, m) });
                    }}
                    className="h-10 text-sm border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
                  />
                </Field>
              </div>

              {cita.fechaFin && (
                <Field label={t('newAppointmentDialog.labelDateEnd')}>
                  <Input
                    type="date"
                    value={cita.fechaFin}
                    disabled={porHoras}
                    onChange={(e) => updateCita({ fechaFin: e.target.value })}
                    className="h-8 text-sm border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white"
                  />
                </Field>
              )}

              <p className="text-[11px] text-gray-400 dark:text-gray-500">
                La hora fin se recalcula automáticamente al cambiar la hora de
                inicio.
              </p>
            </>
          )}
        </div>

        {/* ── OVERFLOW ────────────────────────────────────────────────────── */}
        {overflowDialog && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-black/80 backdrop-blur-sm rounded-t-2xl sm:rounded-xl z-50">
            <div className="w-[90%] mx-auto rounded-xl shadow-xl border border-yellow-200 dark:border-yellow-800 bg-white dark:bg-[#1c1c1e] p-4 space-y-3">
              <h3 className="text-sm font-semibold text-[#103254] dark:text-blue-400">
                {t('newAppointmentDialog.overflowTitle')}
              </h3>
              <div className="rounded-lg border border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-900 dark:text-yellow-200 p-3 text-xs leading-relaxed">
                {overflowInfo?.allowedEnd ? (
                  <>
                    {t('newAppointmentDialog.overflowWorkerLimit')}
                    <br />
                    {t('newAppointmentDialog.overflowWorkerEnd', {
                      hora: overflowInfo.allowedEnd.substring(0, 5),
                    })}
                  </>
                ) : (
                  <>{t('newAppointmentDialog.overflowNextDay')}</>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {overflowInfo?.allowedEnd && (
                  <Button
                    onClick={handleMantener}
                    className="w-full rounded-lg bg-orange-400 hover:bg-orange-500 text-white text-sm font-semibold py-2"
                  >
                    {t('newAppointmentDialog.overflowKeep')}
                  </Button>
                )}
                <Button
                  onClick={handleEditar}
                  className="w-full rounded-lg bg-[#103254] hover:bg-[#0d2742] text-white text-sm font-semibold py-2"
                >
                  {t('newAppointmentDialog.overflowSplit')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── FOOTER ──────────────────────────────────────────────────────── */}
        <DialogFooter className="px-5 pt-2 pb-5 sm:pb-4 flex flex-col gap-1.5 shrink-0 border-t border-gray-100 dark:border-white/10">
          <div className="flex flex-row gap-2 w-full">
            {step === 2 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-12 sm:h-9 rounded-xl border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 text-base sm:text-sm font-medium"
              >
                {t('newAppointmentDialog.back', 'Atrás')}
              </Button>
            )}
            {step === 1 ? (
              <Button
                type="button"
                onClick={() => setStep(2)}
                disabled={!cita.servicio}
                className="w-full h-12 sm:h-9 rounded-xl bg-[#103254] hover:bg-[#0d2742] text-white text-base sm:text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('newAppointmentDialog.next', 'Siguiente')}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={guardarCita}
                disabled={loading}
                className="flex-1 h-12 sm:h-9 rounded-xl bg-[#103254] hover:bg-[#0d2742] text-white text-base sm:text-sm font-semibold disabled:opacity-50"
              >
                {loading
                  ? t('newAppointmentDialog.saving')
                  : t('newAppointmentDialog.saveBtn')}
              </Button>
            )}
          </div>
          {step === 1 && !cita.servicio && (
            <p className="text-[11px] text-center text-gray-400 dark:text-gray-500">
              {t(
                'newAppointmentDialog.serviceRequiredHint',
                'Elige un servicio para activar "Siguiente"'
              )}
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}